import { useModel } from "@stem-sims/nexus"
import Paginator from "components/Dashboards/General/Pagination/Paginator"
import MasteryBar from "components/Dashboards/General/Science35/MasteryBar"
import StandardsMasteryColorLengend from "components/Dashboards/General/StandardsMasteryColorLengend"
import ToolTip from "components/General/ToolTip"
import { Table } from "react-bootstrap"
import { BeatLoader } from "react-spinners"
import { scienceClassMasteryData } from "shared/routes/science35"
import { getStudents } from "shared/routes/teacher"
import { ClassStandardMastery } from "shared/types/science35Types"
import { ClassResponse } from "shared/types/teacherTypes"
import { useParams } from "react-router-dom"
import { toBlockType } from "helpers/blockStringConverter"

interface Props {
    activeClass: ClassResponse
}

const ClassMasteryPage = ({ activeClass }: Props) => {
    const { block } = useParams() as { block: string }

    const blockType = toBlockType(block)

    const { response: data, loading } = useModel({
        model: scienceClassMasteryData,
        props: { classId: activeClass.id, targetBlock: blockType },
        deps: [blockType],
        onError: (err) => console.error(err),
    })

    const { response: totalStudents, loading: loadingStudents } = useModel({
        model: getStudents,
        props: { classID: activeClass.id },
        onError: (err) => console.error(err),
    })

    if (loading || !data || loadingStudents || !totalStudents) return <BeatLoader />

    const totalStudentsNumber = totalStudents.length

    const renderRow = (standard: ClassStandardMastery, index: number) => {
        const allResults = standard.masteryResults.concat(standard.initialMasteryResults)
        const masteryPercentage = (allResults.reduce((acc, curr) => {
                return curr.mastery === "Mastered" || curr.mastery === "Exceeds Mastery" ? acc + curr.students.length : acc
            }, 0) / (totalStudentsNumber ? totalStudentsNumber : 1) * 100
        ).toFixed(0)

        const studentsTakenSet = allResults.reduce((acc, result) => {
            result.students.forEach(s => acc.add(s))
            return acc
        }, new Set<string>())
        const studentsTakenArray = Array.from(studentsTakenSet)

        return (
            <tr key={index} className="w-100 p-5 align-middle">
                <td className="text-primary"><ToolTip title={standard.standard?.description}><span>{standard.standard?.subtopicCode}</span></ToolTip></td>
                <td>{studentsTakenArray.length}/{totalStudentsNumber}</td>
                <td>
                    <div className="text-end">
                        <div>Knowledge&nbsp;Check</div>
                        <div>Recheck</div>
                    </div>
                </td>
                <td>
                    <MasteryBar total={totalStudentsNumber} results={standard.initialMasteryResults.map(m => ({mastery: m.mastery, items: m.students}))} />
                    <div className="mt-2"></div>
                    <MasteryBar total={totalStudentsNumber} results={standard.masteryResults.map(m => ({mastery: m.mastery, items: m.students}))} />
                </td>
                <td>{masteryPercentage}%</td>
            </tr>
        )
    }

  return (
    <div className="mt-3">
        <h1><i className="fa-solid fa-school"></i> Class Mastery Page</h1>
        <Table className="mt-5" responsive>
            <thead>
                <tr>
                    <th>Standard</th>
                    <th>Completion<br />(# of students)</th>
                    <th>Label</th>
                    <th>Mastery Results</th>
                    <th>Class Mastery</th>
                </tr>
            </thead>
            <tbody>
                <Paginator items={data.classStandardMasteries} renderItem={renderRow} position="up" itemsPerPage={8} />
            </tbody>
        </Table>
        <div className="d-flex justify-content-center">
            <StandardsMasteryColorLengend />
        </div>
    </div>
  )
}

export default ClassMasteryPage
